/**
 * Demo page for Integration Monitoring Dashboard
 * Shows how to use the monitoring service and dashboard components
 */

import React, { useEffect, useState } from 'react';
import IntegrationMonitoringDashboard from './IntegrationMonitoringDashboard';
import { integrationMonitoringService } from '../../services/monitoring/IntegrationMonitoringService';
import { Play, Square, RefreshCw } from 'lucide-react';

const MonitoringDashboardDemo: React.FC = () => {
  const [demoRunning, setDemoRunning] = useState(false);
  const [demoInterval, setDemoInterval] = useState<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Initialize demo integrations
    initializeDemoData();
    
    return () => {
      if (demoInterval) {
        clearInterval(demoInterval);
      }
    };
  }, [demoInterval]);

  const initializeDemoData = () => {
    // Register demo integrations
    integrationMonitoringService.registerIntegration({
      id: 'salesforce-demo',
      name: 'Salesforce CRM',
      type: 'crm',
      platform: 'salesforce',
      status: 'healthy',
      healthScore: 98,
      lastSync: new Date(Date.now() - 1000 * 60 * 5), // 5 minutes ago
      syncSuccess: 99.2,
      errorRate: 0.8,
      responseTime: 245,
      uptime: 99.9,
      recordsProcessed: 15420,
      recordsPerHour: 1250
    });

    integrationMonitoringService.registerIntegration({
      id: 'hubspot-demo',
      name: 'HubSpot CRM',
      type: 'crm',
      platform: 'hubspot',
      status: 'warning',
      healthScore: 85,
      lastSync: new Date(Date.now() - 1000 * 60 * 15), // 15 minutes ago
      syncSuccess: 94.5,
      errorRate: 5.5,
      responseTime: 890,
      uptime: 98.2,
      recordsProcessed: 8930,
      recordsPerHour: 720
    });

    integrationMonitoringService.registerIntegration({
      id: 'shopify-demo',
      name: 'Shopify Store',
      type: 'ecommerce',
      platform: 'shopify',
      status: 'healthy',
      healthScore: 96,
      lastSync: new Date(Date.now() - 1000 * 60 * 2), // 2 minutes ago
      syncSuccess: 98.8,
      errorRate: 1.2,
      responseTime: 320,
      uptime: 99.7,
      recordsProcessed: 23450,
      recordsPerHour: 1890
    });

    integrationMonitoringService.registerIntegration({
      id: 'facebook-demo',
      name: 'Facebook & Instagram',
      type: 'social',
      platform: 'facebook',
      status: 'error',
      healthScore: 45,
      lastSync: new Date(Date.now() - 1000 * 60 * 60 * 2), // 2 hours ago
      syncSuccess: 78.3,
      errorRate: 21.7,
      responseTime: 1250,
      uptime: 95.1,
      recordsProcessed: 5670,
      recordsPerHour: 340
    });

    // Add some alert rules
    integrationMonitoringService.addAlertRule({
      name: 'High Error Rate',
      condition: 'error_rate > threshold',
      threshold: 10,
      severity: 'high',
      enabled: true
    });

    integrationMonitoringService.addAlertRule({
      name: 'Sync Failure',
      condition: 'sync_failed',
      threshold: 1,
      severity: 'critical',
      enabled: true
    });

    integrationMonitoringService.addAlertRule({
      name: 'Response Time Degradation',
      condition: 'response_time > threshold',
      threshold: 1000,
      severity: 'medium',
      enabled: true
    });

    // Record some initial sync operations
    integrationMonitoringService.recordSyncOperation({
      integrationId: 'salesforce-demo',
      integrationName: 'Salesforce CRM',
      type: 'incremental_sync',
      direction: 'bidirectional',
      status: 'running',
      startedAt: new Date(Date.now() - 1000 * 60 * 3),
      recordsProcessed: 1250,
      recordsSucceeded: 1248,
      recordsFailed: 2,
      errors: [],
      progress: 75
    });

    integrationMonitoringService.recordSyncOperation({
      integrationId: 'hubspot-demo',
      integrationName: 'HubSpot CRM',
      type: 'full_sync',
      direction: 'from_platform',
      status: 'completed',
      startedAt: new Date(Date.now() - 1000 * 60 * 45),
      completedAt: new Date(Date.now() - 1000 * 60 * 30),
      recordsProcessed: 8930,
      recordsSucceeded: 8845,
      recordsFailed: 85,
      errors: []
    });

    // Record some errors
    integrationMonitoringService.recordError({
      integrationId: 'hubspot-demo',
      level: 'warning',
      message: 'Rate limit exceeded, retrying with backoff',
      details: 'HubSpot API rate limit of 100 requests per 10 seconds exceeded',
      resolved: true,
      resolution: 'Automatically retried after 30 seconds'
    });

    integrationMonitoringService.recordError({
      integrationId: 'facebook-demo',
      level: 'error',
      message: 'Authentication failed',
      details: 'Facebook access token has expired and needs to be refreshed',
      resolved: false
    });
  };

  const startDemo = () => {
    if (demoRunning) return;
    
    setDemoRunning(true);
    
    // Simulate real-time activity
    const interval = setInterval(() => {
      simulateActivity();
    }, 5000); // Every 5 seconds
    
    setDemoInterval(interval);
  };

  const stopDemo = () => {
    if (demoInterval) {
      clearInterval(demoInterval);
      setDemoInterval(null);
    }
    setDemoRunning(false);
  };

  const simulateActivity = () => {
    const integrations = ['salesforce-demo', 'hubspot-demo', 'shopify-demo', 'facebook-demo'];
    const randomIntegration = integrations[Math.floor(Math.random() * integrations.length)];
    
    // Randomly simulate different activities
    const activity = Math.random();
    
    if (activity < 0.3) {
      // Start a new sync operation
      const syncTypes = ['incremental_sync', 'real_time_update', 'full_sync'];
      const directions = ['bidirectional', 'to_platform', 'from_platform'];
      
      integrationMonitoringService.recordSyncOperation({
        integrationId: randomIntegration,
        integrationName: getIntegrationName(randomIntegration),
        type: syncTypes[Math.floor(Math.random() * syncTypes.length)] as any,
        direction: directions[Math.floor(Math.random() * directions.length)] as any,
        status: 'running',
        startedAt: new Date(),
        recordsProcessed: Math.floor(Math.random() * 1000),
        recordsSucceeded: Math.floor(Math.random() * 950),
        recordsFailed: Math.floor(Math.random() * 50),
        errors: [],
        progress: Math.floor(Math.random() * 100)
      });
    } else if (activity < 0.6) {
      // Complete a running sync operation
      const operations = integrationMonitoringService.getSyncOperations();
      const runningOps = operations.filter(op => op.status === 'running');
      
      if (runningOps.length > 0) {
        const op = runningOps[Math.floor(Math.random() * runningOps.length)];
        const success = Math.random() > 0.1; // 90% success rate
        
        integrationMonitoringService.updateSyncOperation(op.id, {
          status: success ? 'completed' : 'failed',
          completedAt: new Date(),
          progress: 100,
          recordsProcessed: op.recordsProcessed + Math.floor(Math.random() * 100),
          recordsSucceeded: success ? op.recordsProcessed : op.recordsSucceeded,
          recordsFailed: success ? 0 : Math.floor(Math.random() * 50)
        });
      }
    } else if (activity < 0.8) {
      // Record a random error
      const errorLevels = ['error', 'warning', 'info'];
      const errorMessages = [
        'Rate limit exceeded',
        'Connection timeout',
        'Invalid data format',
        'Authentication token expired',
        'API endpoint unavailable',
        'Data validation failed'
      ];
      
      integrationMonitoringService.recordError({
        integrationId: randomIntegration,
        level: errorLevels[Math.floor(Math.random() * errorLevels.length)] as any,
        message: errorMessages[Math.floor(Math.random() * errorMessages.length)],
        details: `Simulated error for ${getIntegrationName(randomIntegration)}`,
        resolved: Math.random() > 0.5
      });
    }
  };

  const getIntegrationName = (id: string): string => {
    const names: Record<string, string> = {
      'salesforce-demo': 'Salesforce CRM',
      'hubspot-demo': 'HubSpot CRM',
      'shopify-demo': 'Shopify Store',
      'facebook-demo': 'Facebook & Instagram'
    };
    return names[id] || id;
  };

  const resetDemo = () => {
    stopDemo();
    // Clear existing data and reinitialize
    integrationMonitoringService.stopMonitoring();
    setTimeout(() => {
      initializeDemoData();
    }, 1000);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-700">
      {/* Demo Controls */}
      <div className="bg-surface border-b border-line px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-content">Integration Monitoring Demo</h1>
            <p className="text-content-secondary mt-1">
              Real-time monitoring dashboard with simulated integration activity
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded-full ${demoRunning ? 'bg-green-500' : 'bg-gray-400'}`}></div>
              <span className="text-sm text-content-secondary">
                Demo {demoRunning ? 'Running' : 'Stopped'}
              </span>
            </div>
            <button
              onClick={demoRunning ? stopDemo : startDemo}
              className={`px-4 py-2 rounded-lg flex items-center gap-2 ${
                demoRunning 
                  ? 'bg-red-600 text-white hover:bg-red-700' 
                  : 'bg-green-600 text-white hover:bg-green-700'
              }`}
            >
              {demoRunning ? (
                <>
                  <Square className="w-4 h-4" />
                  Stop Demo
                </>
              ) : (
                <>
                  <Play className="w-4 h-4" />
                  Start Demo
                </>
              )}
            </button>
            <button
              onClick={resetDemo}
              className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Reset
            </button>
          </div>
        </div>
      </div>

      {/* Monitoring Dashboard */}
      <IntegrationMonitoringDashboard />
    </div>
  );
};

export default MonitoringDashboardDemo;